/* Send-a-case form · LEFT column.
 * Clinic/dentist contact, patient identifiers, product picker, dates,
 * attached materials, material variant, tooth chart / span / arch, shade,
 * clinical notes. All state lives in the parent via SendCaseContext.
 */
import ProductSelect from "@/components/ProductSelect";
import ToothChart from "@/components/ToothChart";
import AttachedMaterialsPicker from "@/components/AttachedMaterialsPicker";
import DateField from "@/components/DateField";
import ShadePicker from "@/components/ShadePicker";
import { ARCH_OPTIONS } from "@/lib/productCatalog";
import { useAuth } from "@/contexts/AuthContext";
import { WorkflowField } from "./WorkflowField";
import { useSendCase } from "./SendCaseContext";

export function SendCaseFormLeft() {
    const { form, setForm, update, teeth, setTeeth, activeProduct } = useSendCase();
    // Logged-in clients carry a `default_shade_system` preference — flow it
    // into the picker so the form opens on the right tab. Anonymous public
    // submissions fall back to "classical" (the safe default).
    const { user } = useAuth();
    const defaultShadeSystem = user?.default_shade_system || "classical";
    // Iter 69 — multi-dentist clinics get a dropdown instead of a free-text
    // input so the operator picks a real dentist on the roster rather than
    // re-typing the name (and risking typos that break warranty-card lookups).
    // Single-dentist or anonymous public submissions keep the free-text input.
    const roster = Array.isArray(user?.dentists) ? user.dentists.filter((d) => d?.name) : [];
    const useDentistDropdown = roster.length > 1;

    return (
        <div className="lg:col-span-7 space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
                <WorkflowField label="Clinic name" required>
                    <input data-testid="input-clinic-name" required value={form.clinic_name} onChange={update("clinic_name")} className="tr-input" placeholder="Dr. Mehta Dental, Koregaon Park" />
                </WorkflowField>
                <WorkflowField label="Dentist name" required>
                    {useDentistDropdown ? (
                        <select
                            data-testid="input-dentist-name"
                            required
                            value={form.dentist_name}
                            onChange={update("dentist_name")}
                            className="tr-input"
                        >
                            {/* Empty option only shows if the parent hasn't pre-selected
                                one yet (legacy form state); once chosen, the user picks
                                from the real roster. */}
                            {!form.dentist_name && <option value="">Select dentist…</option>}
                            {roster.map((d) => (
                                <option key={d.id || d.name} value={d.name}>{d.name}{d.email ? ` · ${d.email}` : ""}</option>
                            ))}
                        </select>
                    ) : (
                        <input data-testid="input-dentist-name" required value={form.dentist_name} onChange={update("dentist_name")} className="tr-input" placeholder="Dr. Anika Mehta" />
                    )}
                </WorkflowField>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <WorkflowField label="Email" required>
                    <input data-testid="input-email" type="email" required value={form.email} onChange={update("email")} className="tr-input" placeholder="anika@mehtadental.in" />
                </WorkflowField>
                <WorkflowField label="Phone (optional)">
                    <input data-testid="input-phone" value={form.phone} onChange={update("phone")} className="tr-input" placeholder="+974 5xxx xxxx" />
                </WorkflowField>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <WorkflowField label="Patient ID" required>
                    <input data-testid="input-patient-id" required value={form.patient_id} onChange={update("patient_id")} className="tr-input" placeholder="PT-2026-0142" />
                </WorkflowField>
                <WorkflowField label="Patient name (optional)">
                    <input data-testid="input-patient-name" value={form.patient_name} onChange={update("patient_name")} className="tr-input" placeholder="e.g. Aisha Al-Marri" />
                </WorkflowField>
            </div>
            <div className="grid sm:grid-cols-1 gap-4">
                <WorkflowField label="Clinical product" required>
                    <ProductSelect value={form.product} onChange={(slug) => setForm((f) => ({ ...f, product: slug }))} testid="input-product" />
                </WorkflowField>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
                <WorkflowField label="Order date">
                    <DateField
                        testid="input-order-date"
                        value={form.order_date}
                        onChange={(v) => setForm((f) => ({ ...f, order_date: v }))}
                    />
                </WorkflowField>
                <WorkflowField label="Expected delivery">
                    <DateField
                        testid="input-expected-delivery"
                        value={form.expected_delivery}
                        onChange={(v) => setForm((f) => ({ ...f, expected_delivery: v }))}
                        minDate={form.order_date || undefined}
                    />
                </WorkflowField>
            </div>

            <WorkflowField label="Attached physical materials">
                <AttachedMaterialsPicker
                    value={form.attached_materials}
                    onChange={(arr) => setForm((f) => ({ ...f, attached_materials: arr }))}
                />
                <div className="text-[11.5px] text-slate-500 mt-1 leading-snug">
                    Tick everything you&apos;re physically sending with this case (impressions, opposing cast, shade tab, etc) — the lab uses this checklist to confirm the courier bag is complete.
                </div>
            </WorkflowField>

            {activeProduct?.material_options && activeProduct.material_options.length > 0 && (
                <WorkflowField label={`Material / variant${activeProduct.needs === "arch" ? "" : " (optional)"}`}>
                    <div className="flex flex-wrap gap-2" data-testid="material-options">
                        {activeProduct.material_options.map((m) => (
                            <button
                                type="button"
                                key={m}
                                data-testid={`material-${m.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={() => setForm((f) => ({ ...f, material: f.material === m ? "" : m }))}
                                className={`px-3 py-1.5 rounded-full text-[13px] font-medium border transition ${
                                    form.material === m
                                        ? "bg-[#0B1120] text-cyan-300 border-[#0B1120]"
                                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                                }`}
                            >
                                {m}
                            </button>
                        ))}
                    </div>
                </WorkflowField>
            )}

            {activeProduct?.needs === "teeth" && (
                <WorkflowField label="Tooth chart · select all involved units">
                    <ToothChart selected={teeth} onChange={setTeeth} />
                </WorkflowField>
            )}

            {activeProduct?.needs === "span" && (
                <WorkflowField label="Span · first and last tooth (continuous)">
                    <div className="flex items-center gap-3" data-testid="span-row">
                        <select data-testid="span-from" value={form.span_from} onChange={update("span_from")} className="tr-input flex-1">
                            <option value="">First tooth…</option>
                            {Array.from({ length: 32 }, (_, i) => i + 1).map((n) => <option key={`from-${n}`} value={n}>{n}</option>)}
                        </select>
                        <span className="text-slate-400 text-sm">→</span>
                        <select data-testid="span-to" value={form.span_to} onChange={update("span_to")} className="tr-input flex-1">
                            <option value="">Last tooth…</option>
                            {Array.from({ length: 32 }, (_, i) => i + 1).map((n) => <option key={`to-${n}`} value={n}>{n}</option>)}
                        </select>
                    </div>
                    <div className="text-[12px] text-slate-500 mt-2">All teeth between are auto-included as pontics / abutments.</div>
                </WorkflowField>
            )}

            {activeProduct?.needs === "arch" && (
                <WorkflowField label="Arch">
                    <div className="flex flex-wrap gap-2" data-testid="arch-options">
                        {ARCH_OPTIONS.map((o) => (
                            <button
                                type="button"
                                key={o.id}
                                data-testid={`arch-${o.id}`}
                                onClick={() => setForm((f) => ({ ...f, arch: o.id }))}
                                className={`px-4 py-2 rounded-full text-[13px] font-medium border transition ${
                                    form.arch === o.id
                                        ? "bg-[#0B1120] text-cyan-300 border-[#0B1120]"
                                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                                }`}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                </WorkflowField>
            )}

            {activeProduct?.shade && (
                <WorkflowField label="VITA shade">
                    <ShadePicker
                        value={form.shade}
                        onChange={(s) => setForm({ ...form, shade: s })}
                        variant="dark"
                        testIdPrefix="shade"
                        defaultSystem={defaultShadeSystem}
                    />
                </WorkflowField>
            )}

            <WorkflowField label="Clinical notes (optional)">
                <textarea data-testid="input-notes" value={form.notes} onChange={update("notes")} rows={4} className="tr-input resize-none" placeholder="Bite registration notes, occlusal preferences, contacts..." />
            </WorkflowField>
        </div>
    );
}
